<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .announcements-page
    //- Header
    .page-header
      button(@click="router.back()" type="button").back-button
        img(src="@/assets/icons/chevron-right.svg" alt="Back").back-icon
        span Event Announcements
      button(@click="showDialog = true" type="button").send-button
        img(src="@/assets/icons/plus.svg" alt="Add").send-icon
        span Send Announcement

    //- Loading
    .loading-state(v-if="loading && !announcements.length")
      .spinner
      p.loading-text Loading announcements...

    //- Announcements List
    .announcements-list(v-if="announcements.length")
      .announcement-card(v-for="announcement in announcements" :key="announcement.id")
        .announcement-header
          .announcement-subject {{ announcement.subject }}
          .announcement-time {{ formatDateTime(announcement.sentAt) }}
        .announcement-body {{ announcement.body }}

    //- Empty State
    .empty-state(v-else-if="!loading")
      .empty-icon 📢
      h3.empty-title No announcements yet
      p.empty-subtitle Send your first announcement to notify attendees
      button(@click="showDialog = true" type="button").send-first-button Send Announcement

    //- Send Announcement Dialog
    el-dialog(
      v-model="showDialog"
      title="Send Announcement"
      width="500px"
      @close="resetForm"
    )
      el-form(ref="formRef" :model="form" :rules="rules" label-position="top")
        el-form-item(label="Subject" prop="subject")
          el-input(
            v-model="form.subject"
            placeholder="Enter announcement subject"
            maxlength="255"
            show-word-limit
          )
        el-form-item(label="Message" prop="body")
          el-input(
            v-model="form.body"
            type="textarea"
            :rows="6"
            placeholder="Enter your announcement message"
            maxlength="2000"
            show-word-limit
          )
      template(#footer)
        el-button(@click="showDialog = false") Cancel
        el-button(type="primary" @click="handleSend" :loading="sending") Send to All Attendees
</template>

<script lang="ts">
import {
  defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import webAppToast from '@/utils/webAppToast';
import dayjs from 'dayjs';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import { makeRequest } from '@/graphs/index';
import { sendStickerAnnouncement, findStickerAnnouncements } from '@/graphs/gql';

interface Announcement {
  id: string;
  stickerId: string;
  subject: string;
  body: string;
  sentBy: string;
  sentAt: string;
  createdAt: string;
}

export default defineComponent({
  name: 'HostAnnouncements',
  components: { WebAppLayout },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const stickerId = String(route.params.stickerId);
    const loading = ref(false);
    const sending = ref(false);
    const showDialog = ref(false);
    const announcements = ref<Announcement[]>([]);
    const formRef = ref();
    
    const form = ref({
      subject: '',
      body: '',
    });

    const rules = {
      subject: [
        { required: true, message: 'Please enter a subject', trigger: 'blur' },
        { max: 255, message: 'Subject must be 255 characters or less', trigger: 'blur' },
      ],
      body: [
        { required: true, message: 'Please enter a message', trigger: 'blur' },
        { max: 2000, message: 'Message must be 2000 characters or less', trigger: 'blur' },
      ],
    };

    async function fetchAnnouncements() {
      loading.value = true;
      try {
        const response = await makeRequest(
          'query',
          findStickerAnnouncements,
          { stickerId },
          null,
        );
        if (response?.errors) throw new Error(response.errors[0]?.message);
        announcements.value = response?.data?.findStickerAnnouncements || [];
      } catch (err: any) {
        console.error('Failed to load announcements:', err);
        ElMessage.error('Failed to load announcements');
      } finally {
        loading.value = false;
      }
    }

    async function handleSend() {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();
      } catch {
        return;
      }

      sending.value = true;
      try {
        const response = await makeRequest(
          'mutation',
          sendStickerAnnouncement,
          {
            stickerId,
            subject: form.value.subject,
            body: form.value.body,
          },
          null,
        );

        if (response?.errors) throw new Error(response.errors[0]?.message);

        webAppToast.success('Announcement sent successfully');
        showDialog.value = false;
        resetForm();
        
        // Refresh the list
        await fetchAnnouncements();
      } catch (err: any) {
        console.error('Failed to send announcement:', err);
        ElMessage.error(err.message || 'Failed to send announcement');
      } finally {
        sending.value = false;
      }
    }

    function resetForm() {
      form.value.subject = '';
      form.value.body = '';
      if (formRef.value) {
        formRef.value.clearValidate();
      }
    }

    function formatDateTime(date: string) {
      return dayjs(date).format('MMM D, YYYY h:mm A');
    }

    onMounted(fetchAnnouncements);

    return {
      router,
      loading,
      sending,
      showDialog,
      announcements,
      form,
      formRef,
      rules,
      handleSend,
      resetForm,
      formatDateTime,
    };
  },
});
</script>

<style lang="scss" scoped>
.announcements-page {
  min-height: 100vh;
  background: #f5f5f7;
  padding-bottom: 4rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
  gap: 1rem;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.back-icon {
  width: 1.125rem;
  height: 1.125rem;
  transform: rotate(180deg);
  opacity: 0.6;
}

.send-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.02);
  }
}

.send-icon {
  width: 1rem;
  height: 1rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem;
  gap: 1rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #e0e0e0;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.announcements-list {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.announcement-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.announcement-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.announcement-subject {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a1a;
  flex: 1;
}

.announcement-time {
  font-size: 0.8rem;
  color: #6b7280;
  white-space: nowrap;
}

.announcement-body {
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.6;
  white-space: pre-wrap;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem;
  text-align: center;
}

.empty-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  opacity: 0.4;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem;
}

.empty-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0 0 1.5rem;
}

.send-first-button {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.02);
  }
}
</style>
